import type { FunctionComponent, PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';

import { useBoardId } from '@trello/id-context';

import type { BoardPluginsContextValue } from './BoardPluginsContext';
import {
  BoardPluginsContext,
  emptyBoardPluginsContext,
} from './BoardPluginsContext';
import { useBoardPluginsContextQuery } from './BoardPluginsContextQuery.generated';

export const BoardPluginsContextProvider: FunctionComponent<
  PropsWithChildren<unknown>
> = ({ children }) => {
  const boardId = useBoardId();

  const [value, setValue] = useImmer<BoardPluginsContextValue>(
    emptyBoardPluginsContext,
  );

  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, loading, refetch } = useBoardPluginsContextQuery({
    variables: { boardId },
    waitOn: ['None'],
    fetchPolicy: 'cache-first',
  });
  const plugins = data?.board?.plugins;
  const boardPlugins = data?.board?.boardPlugins;

  useEffect(() => {
    const capabilities = new Set(
      plugins?.flatMap((plugin) => plugin.capabilities),
    );
    const enabledPlugins = new Set(
      boardPlugins?.map((plugin) => plugin.idPlugin),
    );

    setValue({
      capabilities,
      enabledPlugins,
    });
  }, [plugins, boardPlugins, setValue]);

  // we need an up to date data.plugins value to keep track of the current
  // Power-Up capabilities. But plugins(filter:enabled) query is not supported with real time updates.
  // https://hello.atlassian.net/wiki/spaces/TRELLOFE/pages/1225107180/Trello+Front+End+GraphQL+support+matrix+for+real-time+updates+of+filtered+list
  // So instead, we refetch it, but only on useEffects after
  // the initial query is done loading.
  const allowRefetch = useRef(false);
  useEffect(() => {
    if (!loading && !allowRefetch.current) {
      allowRefetch.current = true;
    } else if (allowRefetch.current) {
      refetch();
    }
  }, [boardPlugins, loading, refetch]);

  return (
    <BoardPluginsContext.Provider value={value}>
      {children}
    </BoardPluginsContext.Provider>
  );
};
